import React, { useState, useEffect, useRef } from 'react';
import { Send, Hash, Plus, Settings, Users, Smile, Paperclip, X, Reply, ListOrdered, BarChart3, ArrowLeft, Edit, Menu } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../supabaseClient';
import {
  fetchUserChannels,
  fetchChannelMessages,
  fetchChannelMembers,
  sendMessage,
  updateMessage,
  subscribeToMessages,
  subscribeToMessageUpdates,
  subscribeToMessageDeletions,
  subscribeToChannels,
  subscribeToChannelMembers,
  unsubscribeFromChannel,
  subscribeToTyping,
  broadcastTyping,
  extractMentions,
  groupMessagesByDate
} from '../utils/chatHelpers';
import CreateChannelModal from '../components/CreateChannelModal';
import ChannelSettingsModal from '../components/ChannelSettingsModal';
import CreatePollModal from '../components/CreatePollModal';
import CreateListModal from '../components/CreateListModal';
import PollDisplay from '../components/PollDisplay';
import ListDisplay from '../components/ListDisplay';
import { formatSimpleDate } from '../utils/dateFormatter';

/**
 * Team Chat Component
 * 
 * Features:
 * - Real-time messaging with Supabase subscriptions
 * - Message replies with action menu
 * - Reply navigation: Click on a reply preview to scroll to the original message
 * - Message grouping by date and user
 * - Emoji picker with categorized emojis
 * - Multi-line input with Shift+Enter support
 * - User mentions with @ syntax
 * - Message editing indicators
 * - Channel management (create, update, delete)
 * - Member management
 * 
 * Reply System:
 * 1. Click on any message to open the action menu
 * 2. Click the "Reply" button in the action menu
 * 3. A reply preview appears above the input showing the original message
 * 4. Type your reply and send (the message includes a reference to the parent)
 * 5. Reply previews in the chat are clickable and scroll to the original message
 * 6. Original messages are briefly highlighted (2s) when navigated to via reply
 */
const TeamChat = () => {
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [channelMembers, setChannelMembers] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showMembersSidebar, setShowMembersSidebar] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageActionMenu, setMessageActionMenu] = useState(null);
  const [showMentionPopup, setShowMentionPopup] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const [isChannelMember, setIsChannelMember] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
  const [showMobileActions, setShowMobileActions] = useState(false); // Mobile actions toggle
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // Sidebar collapse state
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false); // Mobile sidebar state
  const [isMobile, setIsMobile] = useState(false);

  const messagesEndRef = useRef(null);
  const subscriptionsRef = useRef([]);
  const channelSubscriptionRef = useRef(null);
  const memberSubscriptionRef = useRef(null);

  // Check screen size for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const messageRefs = useRef({});
  const actionMenuRef = useRef(null);
  const mentionPopupRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const typingChannelRef = useRef(null);

  // -- Caching Refs --
  const messagesCache = useRef({});
  const membersCache = useRef({});

  // Switch to chat view on mobile when channel selected
  useEffect(() => {
    if (selectedChannel) {
      setMobileView('chat');
    }
  }, [selectedChannel]);

  // Emoji categories
  const emojiData = {
    'Frequently Used': ['😂', '👍', '❤️', '😊', '🎉', '🔥', '👏', '✨'],
    'Smileys & People': [
      '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
      '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
      '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪',
      '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨',
      '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '🤥',
      '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕'
    ],
    'Animals & Nature': [
      '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼',
      '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔',
      '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺',
      '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞'
    ],
    'Food & Drink': [
      '🍎', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🍈',
      '🍒', '🍑', '🥭', '🍍', '🥥', '🥝', '🍅', '🍆',
      '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🧄',
      '🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🥙'
    ],
    'Activities': [
      '⚽', '🏀', '🏈', '⚾', '🥎', '🎾', '🏐', '🏉',
      '🥏', '🎱', '🏓', '🏸', '🏒', '🏑', '🥍', '🏏',
      '⛳', '🏹', '🎣', '🥊', '🥋', '🎽', '⛸️', '🥌',
      '🎿', '⛷️', '🏂', '🏋️', '🤸', '🤺', '🤾', '🏇'
    ],
    'Objects': [
      '⌚', '📱', '💻', '⌨️', '🖥️', '🖨️', '🖱️', '🖲️',
      '🕹️', '🗜️', '💾', '💿', '📀', '📼', '📷', '📸',
      '📹', '🎥', '📽️', '🎞️', '📞', '☎️', '📟', '📠',
      '📺', '📻', '🎙️', '🎚️', '🎛️', '⏱️', '⏲️', '⏰'
    ]
  };

  // Handle emoji picker close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle message action menu close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setMessageActionMenu(null);
      }
    };

    if (messageActionMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [messageActionMenu]);

  // Handle mention popup close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mentionPopupRef.current &&
        !mentionPopupRef.current.contains(event.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(event.target)
      ) {
        setShowMentionPopup(false);
      }
    };

    if (showMentionPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMentionPopup]);

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
    };
    fetchUser();
  }, []);

  // Mark channel as read
  const markChannelAsRead = async (channelId) => {
    if (!currentUser || !channelId) return;
    
    try {
      await supabase.rpc('update_chat_read_status', {
        p_user_id: currentUser.id,
        p_channel_id: channelId,
        p_timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error marking channel as read:', error);
    }
  };

  // Fetch channels on mount and setup real-time subscription
  useEffect(() => {
    loadChannels();

    // Subscribe to channel changes (create, update, delete)
    const channelSub = subscribeToChannels(async ({ event, channel, channelId }) => {
      console.log('[Channels] Real-time event:', event, channel || channelId);

      if (event === 'INSERT') {
        // Check if user should have access to this channel
        if (channel.is_private) {
          // For private channels, check if current user is a member
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          const { data: memberRecord } = await supabase
            .from('team_chat')
            .select('id')
            .eq('record_type', 'member')
            .eq('channel_id', channel.id)
            .eq('user_id', user.id)
            .single();

          // Only add if user is a member
          if (!memberRecord) return;
        }

        // Add new channel to list
        setChannels(prev => {
          // Prevent duplicates
          if (prev.some(ch => ch.id === channel.id)) return prev;
          return [channel, ...prev];
        });
      } else if (event === 'UPDATE') {
        // Update channel in list
        setChannels(prev => prev.map(ch => ch.id === channel.id ? channel : ch));

        // Update selected channel if it's the one being updated
        setSelectedChannel(prev => prev?.id === channel.id ? channel : prev);
      } else if (event === 'DELETE') {
        // Remove channel from list
        setChannels(prev => prev.filter(ch => ch.id !== channelId));

        // Clear selection if the deleted channel was selected
        setSelectedChannel(prev => prev?.id === channelId ? null : prev);
      }
    });

    channelSubscriptionRef.current = channelSub;

    // Also subscribe to member changes globally to detect when current user is added/removed
    const globalMemberSub = supabase
      .channel('global-members')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_chat',
          filter: 'record_type=eq.member'
        },
        async (payload) => {
          // If current user was added to a channel, reload channels list
          const { data: { user } } = await supabase.auth.getUser();
          if (user && payload.new.user_id === user.id) {
            console.log('[Members] Current user added to channel, reloading channels');
            loadChannels();
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'team_chat',
          filter: 'record_type=eq.member'
        },
        async (payload) => {
          // If current user was removed from a channel
          const { data: { user } } = await supabase.auth.getUser();
          if (user && payload.old && payload.old.user_id === user.id) {
            console.log('[Global] ⚠️ Current user removed from channel:', payload.old.channel_id);

            const channelId = payload.old.channel_id;

            // Reload channels to update membership status
            await loadChannels();

            // If it was the selected channel, immediately update UI
            if (selectedChannel?.id === channelId) {
              console.log('[Global] ⚠️ Removed from SELECTED channel - forcing UI update');

              // Immediately show "Not a member" screen
              setIsChannelMember(false);
              setMessages([]);
              setChannelMembers(prev => prev.filter(m => m.user_id !== user.id));

              // Stop all real-time updates
              cleanupSubscriptions();
              cleanupTypingIndicator();

              console.log('[Global] ✓ UI updated to show "Not a member"');
            }
          }
        }
      )
      .subscribe();

    return () => {
      if (channelSubscriptionRef.current) {
        unsubscribeFromChannel(channelSubscriptionRef.current);
      }
      if (globalMemberSub) {
        supabase.removeChannel(globalMemberSub);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Load selected channel data and reset input states
  useEffect(() => {
    if (selectedChannel) {
      // Clear input states when switching channels
      setMessageInput('');
      setReplyingTo(null);
      setShowMentionPopup(false);
      setMentionStartIndex(-1);
      setMentionSearch('');
      setShowEmojiPicker(false);
      setMessageActionMenu(null);

      // Stop typing indicator for previous channel
      handleTypingIndicator(false);

      loadChannelData();
      setupRealtimeSubscriptions();
      setupTypingIndicator();
      setupMemberSubscription();

      // Save selected channel to localStorage
      localStorage.setItem('lastSelectedChannel', selectedChannel.id);
    }

    return () => {
      cleanupSubscriptions();
      cleanupTypingIndicator();
      cleanupMemberSubscription();
    };
  }, [selectedChannel]);

  // Clear input immediately when membership status changes to false
  useEffect(() => {
    if (!isChannelMember && messageInput) {
      console.log('[Membership] Lost access - clearing message input');
      setMessageInput('');
      setReplyingTo(null);
    }
  }, [isChannelMember]);

  // Auto-scroll to bottom when new messages arrive AND update cache
  useEffect(() => {
    scrollToBottom();
    // Keep cache in sync with current messages
    if (selectedChannel && messages.length > 0) {
      messagesCache.current[selectedChannel.id] = messages;
    }
  }, [messages, selectedChannel]);

  // Auto-scroll when typing indicator appears
  useEffect(() => {
    if (typingUsers.length > 0) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [typingUsers]);

  const loadChannels = async () => {
    setLoading(true);
    const { data, error } = await fetchUserChannels();
    if (!error && data) {
      setChannels(data);

      // Restore last selected channel from localStorage
      const lastChannelId = localStorage.getItem('lastSelectedChannel');
      if (lastChannelId && data.length > 0) {
        const lastChannel = data.find(ch => ch.id === lastChannelId);
        if (lastChannel) {
          setSelectedChannel(lastChannel);
        } else {
          // Fallback to first channel if last selected doesn't exist
          setSelectedChannel(data[0]);
        }
      } else if (data.length > 0 && !selectedChannel) {
        setSelectedChannel(data[0]);
      }
    }
    setLoading(false);
  };

  const loadChannelData = async () => {
    if (!selectedChannel) return;

    const channelId = selectedChannel.id;

    // --- 1. Optimistic Cache Update ---
    // Immediately show cached data while fetching fresh data
    if (messagesCache.current[channelId]) {
      setMessages(messagesCache.current[channelId]);
    } else {
      setMessages([]); // Clear (or show skeleton) if no cache
    }

    if (membersCache.current[channelId]) {
      setChannelMembers(membersCache.current[channelId]);
      const cachedIsMember = membersCache.current[channelId].some(m => m.user_id === currentUser?.id);
      setIsChannelMember(cachedIsMember);
    }

    // --- 2. Parallel Fetching ---
    // Fetch members and messages simultaneously to reduce wait time
    const [membersResult, messagesResult] = await Promise.all([
      fetchChannelMembers(channelId),
      fetchChannelMessages(channelId)
    ]);

    // --- 3. Process Members ---
    let isMember = false;
    if (membersResult.data) {
      setChannelMembers(membersResult.data);
      membersCache.current[channelId] = membersResult.data; // Update cache

      isMember = membersResult.data.some(m => m.user_id === currentUser?.id);
      console.log('[Channel] Membership check:', isMember ? '✓ Member' : '✗ Not a member');
      setIsChannelMember(isMember);
    }

    // --- 4. Process Messages ---
    if (isMember) {
      if (messagesResult.data) {
        setMessages(messagesResult.data);
        messagesCache.current[channelId] = messagesResult.data; // Update cache
      }
      
      // Mark channel as read when messages are loaded
      await markChannelAsRead(channelId);
    } else {
      // If not a member (even after fetch), clear messages
      setMessages([]);
    }
  };

  const refetchMembers = async () => {
    if (!selectedChannel) return;

    console.log('[Members] Refetching members from database');
    const { data: membersData } = await fetchChannelMembers(selectedChannel.id);
    if (membersData) {
      setChannelMembers(membersData);
      console.log('[Members] Refetched', membersData.length, 'members');
    }
  };

  const setupRealtimeSubscriptions = () => {
    if (!selectedChannel) return;

    console.log('[Realtime] Setting up subscriptions for channel:', selectedChannel.id);

    // Subscribe to new messages
    const newMessagesSub = subscribeToMessages(selectedChannel.id, (newMessage) => {
      console.log('[Realtime] New message received:', newMessage);

      // Only process messages if user is still a member
      if (!isChannelMember) {
        console.log('[Realtime] User is not a member, ignoring message');
        return;
      }

      setMessages(prev => {
        // Skip if message already exists (prevents duplicates)
        if (prev.some(msg => msg.id === newMessage.id)) {
          console.log('[Realtime] Message already exists, skipping duplicate');
          return prev;
        }

        // Skip if this is from current user and there's a temp message (optimistic update)
        // The temp message will be replaced by the real one in handleSendMessage
        if (newMessage.user_id === currentUser?.id) {
          const hasTempMessage = prev.some(msg => msg.id.toString().startsWith('temp-'));
          if (hasTempMessage) {
            console.log('[Realtime] Skipping realtime update for own message (optimistic update active)');
            return prev;
          }
        }

        return [...prev, newMessage];
      });
    });

    // Subscribe to message updates
    const updatesSub = subscribeToMessageUpdates(selectedChannel.id, (updatedMessage) => {
      console.log('[Realtime] Message updated:', updatedMessage);
      setMessages(prev => prev.map(msg =>
        msg.id === updatedMessage.id ? { ...msg, ...updatedMessage } : msg
      ));
    });

    // Subscribe to message deletions
    const deletionsSub = subscribeToMessageDeletions(selectedChannel.id, (deletedId) => {
      console.log('[Realtime] Message deleted:', deletedId);
      setMessages(prev => prev.filter(msg => msg.id !== deletedId));
    });

    // Subscribe to poll vote changes (INSERT and UPDATE)
    const votesSub = supabase
      .channel(`votes:${selectedChannel.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'team_chat',
          filter: `channel_id=eq.${selectedChannel.id}`
        },
        async (payload) => {
          if (payload.new.record_type !== 'vote') return;

          console.log('[Realtime] New vote received:', payload);

          // Get current user to check if this is their own vote
          const { data: { user } } = await supabase.auth.getUser();
          
          // Skip if this is the current user's vote (already added optimistically)
          if (user && payload.new.user_id === user.id) {
            console.log('[Realtime] Skipping own vote (already added optimistically)');
            return;
          }

          // Update the poll's votes in the messages (for other users' votes only)
          setMessages(prev => prev.map(msg => {
            if (msg.type === 'poll' && msg.id === payload.new.parent_message_id) {
              return {
                ...msg,
                votes: [...(msg.votes || []), {
                  id: payload.new.id,
                  user_id: payload.new.user_id,
                  vote_option_index: payload.new.vote_option_index,
                  parent_message_id: payload.new.parent_message_id
                }]
              };
            }
            return msg;
          }));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'team_chat',
          filter: `channel_id=eq.${selectedChannel.id}`
        },
        (payload) => {
          if (payload.new.record_type !== 'vote') return;

          console.log('[Realtime] Vote updated:', payload);

          // Update the vote in the poll
          setMessages(prev => prev.map(msg => {
            if (msg.type === 'poll' && msg.id === payload.new.parent_message_id) {
              return {
                ...msg,
                votes: (msg.votes || []).map(v =>
                  v.id === payload.new.id
                    ? { ...v, vote_option_index: payload.new.vote_option_index }
                    : v
                )
              };
            }
            return msg;
          }));
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Votes subscription status:', status);
      });

    subscriptionsRef.current = [newMessagesSub, updatesSub, deletionsSub, votesSub];

    console.log('[Realtime] Subscriptions established:', subscriptionsRef.current.length);
  };

  const cleanupSubscriptions = () => {
    subscriptionsRef.current.forEach(sub => {
      unsubscribeFromChannel(sub);
    });
    subscriptionsRef.current = [];
  };

  /**
   * Setup typing indicator subscription
   */
  const setupTypingIndicator = () => {
    if (!selectedChannel || !currentUser) return;

    console.log('[Typing] Setting up typing indicator for channel:', selectedChannel.id);

    const typingChannel = subscribeToTyping(selectedChannel.id, (payload) => {
      const { user_id, user_name, is_typing, timestamp } = payload;

      // Ignore own typing events
      if (user_id === currentUser.id) return;

      console.log('[Typing] Received typing event:', payload);

      if (is_typing) {
        // Add or update typing user
        setTypingUsers(prev => {
          const filtered = prev.filter(u => u.user_id !== user_id);
          return [...filtered, { user_id, user_name, timestamp }];
        });

        // Auto-remove after 3 seconds of inactivity
        setTimeout(() => {
          setTypingUsers(prev =>
            prev.filter(u => u.user_id !== user_id || Date.now() - u.timestamp < 3000)
          );
        }, 3000);
      } else {
        // Remove typing user
        setTypingUsers(prev => prev.filter(u => u.user_id !== user_id));
      }
    });

    typingChannelRef.current = typingChannel;
  };

  /**
   * Cleanup typing indicator subscription
   */
  const cleanupTypingIndicator = () => {
    if (typingChannelRef.current) {
      unsubscribeFromChannel(typingChannelRef.current);
      typingChannelRef.current = null;
    }
    setTypingUsers([]);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  /**
   * Broadcast typing status
   */
  const handleTypingIndicator = (isTyping) => {
    if (!selectedChannel || !currentUser) return;

    const userName = currentUser.email?.split('@')[0] || 'User';

    broadcastTyping(selectedChannel.id, currentUser.id, userName, isTyping);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Auto-stop typing after 2 seconds of no input
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        broadcastTyping(selectedChannel.id, currentUser.id, userName, false);
      }, 2000);
    }
  };

  /**
   * Setup member subscription for real-time member changes
   */
  const setupMemberSubscription = () => {
    if (!selectedChannel || !currentUser) return;

    console.log('[Members] Setting up subscription for channel:', selectedChannel.id);

    const memberSub = subscribeToChannelMembers(selectedChannel.id, ({ event, member, memberId, userId }) => {
      console.log('[Members] Real-time event:', event, member || memberId);

      if (event === 'INSERT') {
        console.log('[Members] Adding member:', member);
        // Add new member to list
        setChannelMembers(prev => {
          // Prevent duplicates
          if (prev.some(m => m.id === member.id)) {
            console.log('[Members] Member already exists, skipping');
            return prev;
          }
          console.log('[Members] Adding member to list. Current count:', prev.length);
          const updated = [...prev, member];
          console.log('[Members] New count:', updated.length);
          return updated;
        });
      } else if (event === 'DELETE') {
        console.log('[Members] Removing member:', memberId, 'userId:', userId);
        // Remove member from list
        setChannelMembers(prev => {
          const updated = prev.filter(m => m.id !== memberId);
          console.log('[Members] Removed member. Old count:', prev.length, 'New count:', updated.length);
          return updated;
        });

        // If current user was removed, update membership status immediately
        if (userId === currentUser.id) {
          console.log('[Members] ⚠️ CURRENT USER REMOVED FROM CHANNEL - Updating UI');

          // Immediately update membership status
          setIsChannelMember(false);

          // Clear all messages to show "Not a member" screen
          setMessages([]);

          // Cleanup all subscriptions when user is removed
          cleanupSubscriptions();
          cleanupTypingIndicator();

          // Force a state update to ensure UI refreshes
          // This ensures the "Not a member" message displays immediately
          setChannelMembers(prev => prev.filter(m => m.user_id !== currentUser.id));

          console.log('[Members] ✓ UI updated - showing "Not a member" screen');
        }
      } else if (event === 'UPDATE') {
        console.log('[Members] Updating member:', member);
        // Update member role
        setChannelMembers(prev => prev.map(m => m.id === member.id ? member : m));
      }
    });

    memberSubscriptionRef.current = memberSub;
  };

  /**
   * Cleanup member subscription
   */
  const cleanupMemberSubscription = () => {
    if (memberSubscriptionRef.current) {
      unsubscribeFromChannel(memberSubscriptionRef.current);
      memberSubscriptionRef.current = null;
    }
  };

  /**
   * Handle input change in textarea
   * Detects @ mentions and shows autocomplete popup
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setMessageInput(value);

    // Broadcast typing indicator
    if (value.trim()) {
      handleTypingIndicator(true);
    } else {
      handleTypingIndicator(false);
    }

    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);

    // Find the last @ symbol before cursor
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if there's a space between @ and cursor (if so, not a mention)
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

      if (!textAfterAt.includes(' ') && textAfterAt.length <= 20) {
        // Valid mention in progress
        setMentionSearch(textAfterAt.toLowerCase());
        setMentionStartIndex(lastAtIndex);
        setShowMentionPopup(true);
        setSelectedMentionIndex(0);
      } else {
        setShowMentionPopup(false);
      }
    } else {
      setShowMentionPopup(false);
    }
  };

  /**
   * Insert selected mention into textarea
   */
  const insertMention = (member) => {
    if (mentionStartIndex === -1) return;

    const beforeMention = messageInput.substring(0, mentionStartIndex);
    const cursorPosition = textareaRef.current.selectionStart;
    const afterCursor = messageInput.substring(cursorPosition);

    const isInactive = member.user.is_active === false;
    const displayName = isInactive && member.user.inactive_user_name 
      ? member.user.inactive_user_name 
      : (member.user.name || 'Unknown User');
    const mentionText = `@${displayName} `;
    const newText = beforeMention + mentionText + afterCursor;

    setMessageInput(newText);
    setShowMentionPopup(false);
    setMentionStartIndex(-1);

    // Set cursor position after mention
    setTimeout(() => {
      const newCursorPos = beforeMention.length + mentionText.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    // Handle mention popup navigation
    if (showMentionPopup) {
      const filteredMembers = channelMembers.filter(m => {
        const isInactive = m.user.is_active === false;
        const displayName = isInactive && m.user.inactive_user_name 
          ? m.user.inactive_user_name 
          : (m.user.name || '');
        return displayName.toLowerCase().includes(mentionSearch);
      });

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev < filteredMembers.length - 1 ? prev + 1 : 0
        );
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex(prev =>
          prev > 0 ? prev - 1 : filteredMembers.length - 1
        );
        return;
      }

      if (e.key === 'Enter' && filteredMembers.length > 0) {
        e.preventDefault();
        insertMention(filteredMembers[selectedMentionIndex]);
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionPopup(false);
        return;
      }
    }

    // Send message on Enter, allow new line on Shift+Enter
    // On mobile, Enter should produced a new line (default behavior), so we only trap it on desktop
    const isMobile = window.innerWidth < 1024;
    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleInputFocus = (e) => {
    // On mobile, ensure the input stays visible when keyboard opens
    if (window.innerWidth < 1024) {
      // Multiple attempts to scroll into view to handle different keyboard animation speeds
      // and browser behaviors (especially iOS vs Android quirks)
      const scrollIntoView = () => {
        e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      };

      // Attempt 1: Immediate
      scrollIntoView();

      // Attempt 2: Short delay (100ms)
      setTimeout(scrollIntoView, 100);

      // Attempt 3: Medium delay (300ms) - standard keyboard animation
      setTimeout(scrollIntoView, 300);

      // Attempt 4: Long delay (600ms) - slow devices/animations
      setTimeout(scrollIntoView, 600);
    }
  };

  const handleInputClick = (e) => {
    // Also trigger on click to catch cases where focus was already active but keyboard was dismissed
    if (window.innerWidth < 1024) {
      handleInputFocus(e);
    }
  };

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = messageInput.substring(0, cursorPosition);
    const textAfterCursor = messageInput.substring(cursorPosition);

    // Insert emoji at cursor position
    const newText = textBeforeCursor + emoji + textAfterCursor;
    setMessageInput(newText);

    // Close emoji picker
    setShowEmojiPicker(false);

    // Focus textarea and set cursor position after emoji
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = cursorPosition + emoji.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);
  };

  /**
   * Handle showing message action menu
   * @param {Object} message - The message that was clicked
   * @param {Object} event - The click event for positioning
   */
  const handleMessageClick = (message, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMessageActionMenu({
      message,
      x: rect.right - 100,
      y: rect.top
    });
  };

  /**
   * Handle reply to a message
   * @param {Object} message - The message to reply to
   */
  const handleReplyToMessage = (message) => {
    setReplyingTo(message);
    setMessageActionMenu(null);
    textareaRef.current?.focus();
  };

  /**
   * Cancel the current reply
   */
  const handleCancelReply = () => {
    setReplyingTo(null);
  };

  /**
   * Handle edit a message
   * @param {Object} message - The message to edit
   */
  const handleEditMessage = (message) => {
    setEditingMessage(message);
    setMessageInput(message.message);
    setMessageActionMenu(null);
    textareaRef.current?.focus();
  };

  /**
   * Cancel the current edit
   */
  const handleCancelEdit = () => {
    setEditingMessage(null);
    setMessageInput('');
  };

  /**
   * Scroll to a specific message in the chat history
   * @param {string} messageId - The ID of the message to scroll to
   */
  const scrollToMessage = (messageId) => {
    const messageElement = messageRefs.current[messageId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Highlight the message briefly
      messageElement.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    // Block sending if not a channel member (extra guard)
    if (!isChannelMember) {
      alert('You are not a member of this channel.');
      return;
    }
    if (!messageInput.trim() || !selectedChannel || sendingMessage) return;

    const messageText = messageInput.trim();

    // Handle message editing
    if (editingMessage) {
      setSendingMessage(true);

      const { data, error } = await updateMessage(editingMessage.id, messageText);

      if (error) {
        alert('Failed to edit message. Please try again.');
      } else if (data) {
        // Update the message in the UI
        setMessages(prev => prev.map(msg => 
          msg.id === editingMessage.id 
            ? { ...msg, message: messageText, is_edited: true } 
            : msg
        ));
      }

      setMessageInput('');
      setEditingMessage(null);
      setSendingMessage(false);
      return;
    }

    // Handle normal message sending
    const parentMessageId = replyingTo?.id || null;

    setMessageInput('');
    setReplyingTo(null);

    // Stop typing indicator
    handleTypingIndicator(false);

    // Create optimistic message for instant display
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      channel_id: selectedChannel.id,
      user_id: currentUser?.id,
      message: messageText,
      parent_message_id: parentMessageId,
      created_at: new Date().toISOString(),
      is_edited: false,
      user: {
        id: currentUser?.id,
        name: currentUser?.email?.split('@')[0] || 'You',
        email: currentUser?.email
      }
    };

    // Add parent message data if replying
    if (replyingTo) {
      optimisticMessage.parent_message = {
        id: replyingTo.id,
        message: replyingTo.message,
        user: replyingTo.user
      };
    }

    // Add message to UI instantly
    setMessages(prev => [...prev, optimisticMessage]);

    // Scroll to bottom
    setTimeout(() => scrollToBottom(), 50);

    setSendingMessage(true);

    // Extract mentions
    const allUsers = channelMembers.map(m => m.user);
    const mentions = extractMentions(messageText, allUsers);

    const { data, error } = await sendMessage(selectedChannel.id, messageText, mentions, parentMessageId);

    if (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== optimisticMessage.id));
      setMessageInput(messageText);
      setReplyingTo(replyingTo);
      alert('Failed to send message. Please try again.');
    } else if (data) {
      // Replace optimistic message with real one from database
      setMessages(prev => prev.map(m =>
        m.id === optimisticMessage.id ? { ...data, type: 'message' } : m
      ));
      console.log('[SendMessage] Replaced optimistic message with real message:', data.id);
      
      // Mark channel as read after sending message
      markChannelAsRead(selectedChannel.id);
    }

    setSendingMessage(false);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * Handle voting on a poll
   */
  const handlePollVote = async (pollId, optionIndex) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if user already voted
      const { data: existingVote } = await supabase
        .from('team_chat')
        .select('*')
        .eq('record_type', 'vote')
        .eq('parent_message_id', pollId)
        .eq('user_id', user.id)
        .single();

      if (existingVote) {
        // Update existing vote
        await supabase
          .from('team_chat')
          .update({ vote_option_index: optionIndex })
          .eq('id', existingVote.id);

        // Optimistically update local state
        setMessages(prev => prev.map(msg => {
          if (msg.type === 'poll' && msg.id === pollId) {
            return {
              ...msg,
              votes: (msg.votes || []).map(v =>
                v.id === existingVote.id
                  ? { ...v, vote_option_index: optionIndex }
                  : v
              )
            };
          }
          return msg;
        }));
      } else {
        // Create new vote
        const { data: newVote } = await supabase
          .from('team_chat')
          .insert({
            record_type: 'vote',
            channel_id: selectedChannel.id,
            user_id: user.id,
            parent_message_id: pollId,
            vote_option_index: optionIndex,
            created_by: user.id
          })
          .select()
          .single();

        // Optimistically update local state
        if (newVote) {
          setMessages(prev => prev.map(msg => {
            if (msg.type === 'poll' && msg.id === pollId) {
              return {
                ...msg,
                votes: [...(msg.votes || []), {
                  id: newVote.id,
                  user_id: newVote.user_id,
                  vote_option_index: newVote.vote_option_index,
                  parent_message_id: newVote.parent_message_id
                }]
              };
            }
            return msg;
          }));
        }
      }

      // Real-time subscription will update for other users
    } catch (error) {
      console.error('Error voting on poll:', error);
    }
  };

  const handleChannelCreated = (newChannel) => {
    setChannels(prev => [newChannel, ...prev]);
    setSelectedChannel(newChannel);
    setShowCreateModal(false);
  };

  // Group messages by date
  const groupedMessages = groupMessagesByDate(messages);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-400">Loading channels...</div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col bg-[#0a0e1a] overflow-hidden relative">
      {/* Coming Soon Overlay */}
      <div className="coming-soon-overlay">
        <div className="coming-soon-text">Coming Soon</div>
      </div>
      <div className="flex flex-1 overflow-hidden relative">
        {/* Sidebar Backdrop for Mobile */}
        {isMobile && mobileSidebarOpen && (
          <div
            className="absolute inset-0 bg-black/60 z-20"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
        {/* Channel Sidebar */}
        <div className={`
          ${isMobile ? 'absolute inset-y-0 left-0 z-30 w-[80%] max-w-[300px] shadow-2xl' : sidebarCollapsed ? 'w-16' : 'w-64'}
          glass-card border-r border-white/10 flex flex-col transition-all duration-300
          ${isMobile && !mobileSidebarOpen ? '-translate-x-full' : 'translate-x-0'}
        `}>
          {/* Sidebar Header */}
          <div className={`${sidebarCollapsed && !isMobile ? 'p-2' : 'p-4'} border-b border-white/10 flex-shrink-0`}>
            {/* Hamburger Menu Button */}
            <div className={`flex items-center ${sidebarCollapsed && !isMobile ? 'justify-center' : 'justify-between'} mb-3`}>
              {(!sidebarCollapsed || isMobile) && (
                <h2 className="text-xl font-bold text-white">Team Chat</h2>
              )}
              {!isMobile && (
                <button
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                  title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                  {sidebarCollapsed ? <Menu className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                </button>
              )}
              {isMobile && (
                <button
                  onClick={() => setMobileSidebarOpen(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
            </div>
            {(!sidebarCollapsed || isMobile) && (
              <button
                onClick={() => {
                  setShowCreateModal(true);
                  if (isMobile) setMobileSidebarOpen(false);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white hover:bg-gray-200 rounded-lg text-black text-sm font-bold transition-colors border border-white/10 shadow-[0_0_15px_rgba(255,255,255,0.05)]"
              >
                <Plus className="w-4 h-4" />
                New Channel
              </button>
            )}
            {sidebarCollapsed && !isMobile && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-full flex items-center justify-center p-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors mt-2"
                title="New Channel"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Channels List */}
          {(!sidebarCollapsed || isMobile) && (
            <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {channels.length === 0 ? (
                <div className="p-4 text-center text-gray-400 text-sm">
                  No channels yet. Create one to get started!
                </div>
              ) : (
                <div className="p-2">
                  {channels.map(channel => (
                    <button
                      key={channel.id}
                      onClick={() => {
                        setSelectedChannel(channel);
                        if (isMobile) setMobileSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 ${
                        selectedChannel?.id === channel.id
                          ? 'bg-white text-black shadow-[0_0_15px_rgba(255,255,255,0.1)] font-bold'
                          : 'hover:bg-white/5 text-gray-300 hover:text-white'
                      }`}
                    >
                      <Hash className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium truncate">{channel.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Collapsed Sidebar Actions */}
          {sidebarCollapsed && !isMobile && (
            <div className="flex flex-col items-center p-2 space-y-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="w-10 h-10 bg-white hover:bg-gray-200 text-black rounded-lg flex items-center justify-center transition-colors shadow-[0_0_15px_rgba(255,255,255,0.05)]"
                title="New Channel"
              >
                <Plus size={20} />
              </button>
              {channels.map(channel => (
                <button
                  key={channel.id}
                  onClick={() => setSelectedChannel(channel)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                    selectedChannel?.id === channel.id
                      ? 'bg-white text-black font-bold'
                      : 'hover:bg-white/10 text-gray-400'
                  }`}
                  title={channel.name}
                >
                  <Hash size={16} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Main Chat Area */}
        {selectedChannel ? (
          <div className="flex-1 flex flex-col overflow-hidden relative">
            {/* Chat Header */}
            <div className="border-b border-white/10 px-4 py-3 flex-shrink-0 bg-[#0a0e1a]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {isMobile && (
                    <button
                      onClick={() => setMobileSidebarOpen(true)}
                      className="p-1 -ml-1 text-gray-400 hover:text-white"
                    >
                      <Menu className="w-6 h-6" />
                    </button>
                  )}
                  <Hash className="w-6 h-6 text-gray-400" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">{selectedChannel.name}</h3>
                    {selectedChannel.description && (
                      <p className="text-xs text-gray-400 mt-0.5">{selectedChannel.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      refetchMembers();
                      setShowMembersSidebar(true);
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
                    title={`${channelMembers.length} members`}
                  >
                    <Users className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </button>
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors group"
                    title="Channel settings"
                  >
                    <Settings className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Area - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
              {!isChannelMember ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center max-w-md px-6">
                    <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Not a Member
                    </h3>
                    <p className="text-sm text-gray-400">
                      You are not a member of this channel.
                      <br />
                      Contact <span className="text-white font-medium">
                        {(() => {
                          const creator = channelMembers.find(m => m.user_id === selectedChannel?.created_by);
                          if (!creator?.user) return 'the channel creator';
                          const isInactive = creator.user.is_active === false;
                          return isInactive && creator.user.inactive_user_name 
                            ? creator.user.inactive_user_name 
                            : (creator.user.name || 'the channel creator');
                        })()}
                      </span> to request access.
                    </p>
                  </div>
                </div>
              ) : Object.keys(groupedMessages).length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-gray-500 mb-4">
                      <Hash className="w-16 h-16 mx-auto opacity-20" />
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Welcome to #{selectedChannel.name}
                    </h3>
                    <p className="text-sm text-gray-400">
                      This is the beginning of this channel. Start the conversation!
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-0">
                  {Object.entries(groupedMessages).map(([date, dateMessages]) => (
                    <div key={date}>
                      {/* Date Separator */}
                      <div className="flex items-center gap-4 my-4 first:mt-0">
                        <div className="flex-1 h-px bg-white/10"></div>
                        <span className="text-xs font-semibold text-gray-400 px-2">
                          {date}
                        </span>
                        <div className="flex-1 h-px bg-white/10"></div>
                      </div>

                      {/* Messages and Polls */}
                      {dateMessages.map((item, idx) => {
                        const prevItem = idx > 0 ? dateMessages[idx - 1] : null;
                        const showHeader = !prevItem || prevItem.user_id !== item.user_id || item.type === 'poll';

                        // Render System Message
                        if (item.is_system_message) {
                          return (
                            <div key={item.id} className="flex justify-center my-6">
                              <div className="px-6 py-3 bg-white/5 border border-white/20 rounded-full shadow-lg backdrop-blur-sm">
                                <span className="text-sm font-medium text-white">
                                  {item.message}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        // Render Poll
                        if (item.type === 'poll') {
                          return (
                            <div key={item.id} ref={(el) => messageRefs.current[item.id] = el} className="mt-4">
                              <div className="flex items-baseline gap-2 mb-2 px-2">
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold flex-shrink-0 border border-white/10">
                                  {(() => {
                                    const isInactive = item.user?.is_active === false;
                                    const displayName = isInactive && item.user?.inactive_user_name 
                                      ? item.user.inactive_user_name 
                                      : (item.user?.name || 'U');
                                    return displayName.charAt(0).toUpperCase();
                                  })()}
                                </div>
                                <div>
                                  <span className="text-sm font-semibold text-white">
                                    {(() => {
                                      const isInactive = item.user?.is_active === false;
                                      return isInactive && item.user?.inactive_user_name 
                                        ? item.user.inactive_user_name 
                                        : (item.user?.name || 'Unknown User');
                                    })()}
                                  </span>
                                  <span className="text-xs text-gray-500 ml-2">
                                    {new Date(item.created_at).toLocaleString('en-US', {
                                      month: 'numeric',
                                      day: 'numeric',
                                      year: '2-digit',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    })}
                                  </span>
                                </div>
                              </div>
                              <div className="ml-12">
                                <PollDisplay
                                  poll={item}
                                  currentUserId={currentUser?.id}
                                  onVote={handlePollVote}
                                />
                              </div>
                            </div>
                          );
                        }

                        // Render List
                        if (item.type === 'list') {
                          return (
                            <div key={item.id} className={`group flex gap-3 px-2 ${showHeader ? 'mt-4' : ''}`}>
                              {showHeader && (
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold flex-shrink-0 border border-white/10">
                                  {(() => {
                                    const isInactive = item.user?.is_active === false;
                                    const displayName = isInactive && item.user?.inactive_user_name 
                                      ? item.user.inactive_user_name 
                                      : (item.user?.name || 'U');
                                    return displayName[0] || 'U';
                                  })()}
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {showHeader && (
                                  <div className="flex items-baseline gap-2 mb-1">
                                    <span className="font-semibold text-white">
                                      {(() => {
                                        const isInactive = item.user?.is_active === false;
                                        return isInactive && item.user?.inactive_user_name 
                                          ? item.user.inactive_user_name 
                                          : (item.user?.name || item.user?.email?.split('@')[0] || 'Unknown User');
                                      })()}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {format(new Date(item.created_at), 'h:mm a')}
                                    </span>
                                  </div>
                                )}
                                <div className={showHeader ? '' : 'ml-12'}>
                                  <ListDisplay
                                    list={item}
                                    currentUserId={currentUser?.id}
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        }

                        // Render Message
                        return (
                          <div
                            key={item.id}
                            ref={(el) => messageRefs.current[item.id] = el}
                            onClick={(e) => handleMessageClick(item, e)}
                            className={`group flex gap-3 px-2 py-0.5 hover:bg-white/5 transition-all cursor-pointer rounded-lg relative ${showHeader ? 'mt-4' : ''
                              }`}
                          >
                            {/* Avatar */}
                            <div className="flex-shrink-0 w-10 mt-0.5">
                              {showHeader && (
                                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white font-semibold border border-white/10">
                                  {(() => {
                                    const isInactive = item.user?.is_active === false;
                                    const displayName = isInactive && item.user?.inactive_user_name 
                                      ? item.user.inactive_user_name 
                                      : (item.user?.name || 'U');
                                    return displayName.charAt(0).toUpperCase();
                                  })()}
                                </div>
                              )}
                            </div>

                            {/* Timestamp for continuation messages - shows on hover */}
                            {!showHeader && (
                              <div className="absolute left-2 top-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <span className="text-xs text-gray-500">
                                  {new Date(item.created_at).toLocaleTimeString('en-US', {
                                    hour: 'numeric',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </span>
                              </div>
                            )}

                            {/* Message Content */}
                            <div className="flex-1 min-w-0">
                              {showHeader && (
                                <div className="flex items-baseline gap-2 mb-1">
                                  <span className="text-sm font-semibold text-white hover:underline cursor-pointer">
                                    {(() => {
                                      const isInactive = item.user?.is_active === false;
                                      return isInactive && item.user?.inactive_user_name 
                                        ? item.user.inactive_user_name 
                                        : (item.user?.name || 'Unknown User');
                                    })()}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(item.created_at).toLocaleString('en-US', {
                                      month: 'numeric',
                                      day: 'numeric',
                                      year: '2-digit',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    })}
                                  </span>
                                </div>
                              )}

                              {/* Parent Message Preview (if replying) */}
                              {item.parent_message && (
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    scrollToMessage(item.parent_message_id);
                                  }}
                                  className="mb-2 px-3 py-2 bg-white/5 border-l-2 border-gray-500 rounded text-xs cursor-pointer hover:bg-white/10 transition-colors"
                                >
                                  <div className="font-medium text-gray-400 mb-0.5">
                                    {(() => {
                                      const user = item.parent_message.user;
                                      if (!user) return 'Unknown User';
                                      const isInactive = user.is_active === false;
                                      return isInactive && user.inactive_user_name 
                                        ? user.inactive_user_name 
                                        : (user.name || 'Unknown User');
                                    })()}
                                  </div>
                                  <div className="text-gray-500 truncate">
                                    {item.parent_message.message}
                                  </div>
                                </div>
                              )}

                              <div className="text-sm text-gray-100 leading-relaxed break-words whitespace-pre-wrap">
                                <p
                                  dangerouslySetInnerHTML={{
                                    __html: item.message.replace(
                                      /@(\w+)/g,
                                      '<span class="font-medium text-white bg-white/10 px-1 rounded">@$1</span>'
                                    )
                                  }}
                                />
                                {item.is_edited && (
                                  <span className="text-xs text-gray-500 ml-1">(edited)</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Typing Indicator */}
              {typingUsers.length > 0 && (
                <div className="px-4 py-2">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span>
                      {typingUsers.length === 1
                        ? `${typingUsers[0].user_name} is typing`
                        : typingUsers.length === 2
                          ? `${typingUsers[0].user_name} and ${typingUsers[1].user_name} are typing`
                          : `${typingUsers.length} people are typing`}
                    </span>
                    <div className="flex gap-1">
                      <span className="typing-dot"></span>
                      <span className="typing-dot" style={{ animationDelay: '0.2s' }}></span>
                      <span className="typing-dot" style={{ animationDelay: '0.4s' }}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Message Action Menu */}
            {messageActionMenu && (
              <div
                ref={actionMenuRef}
                className="fixed bg-black border border-white/20 rounded-lg shadow-2xl py-1 z-50 min-w-[140px]"
                style={{
                  left: `${messageActionMenu.x}px`,
                  top: `${messageActionMenu.y}px`
                }}
              >
                <button
                  onClick={() => handleReplyToMessage(messageActionMenu.message)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors text-xs"
                >
                  <Reply className="w-3.5 h-3.5" />
                  <span>Reply</span>
                </button>
                {/* Show edit option only if it's the user's message and it's the last message */}
                {messageActionMenu.message.user_id === currentUser?.id && 
                 messageActionMenu.message.id === messages[messages.length - 1]?.id && (
                  <button
                    onClick={() => handleEditMessage(messageActionMenu.message)}
                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-white/10 text-gray-300 hover:text-white transition-colors text-xs"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    <span>Edit</span>
                  </button>
                )}
              </div>
            )}

            {/* Message Input - Fixed at Bottom - Only visible to channel members */}
            {isChannelMember && (
              <div className="border-t border-white/10 p-2 md:p-4 flex-shrink-0 bg-black z-20">
                {/* Edit Preview */}
                {editingMessage && (
                  <div className="mb-3 px-3 py-2 bg-white/5 border-l-4 border-yellow-500 rounded">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-yellow-400 mb-1">
                          Editing message
                        </div>
                        <div className="text-sm text-gray-400 truncate">
                          {editingMessage.message}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCancelEdit}
                        className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                        title="Cancel edit"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Reply Preview */}
                {replyingTo && !editingMessage && (
                  <div className="mb-3 px-3 py-2 bg-white/5 border-l-4 border-blue-500 rounded">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-blue-400 mb-1">
                          Replying to {(() => {
                            const isInactive = replyingTo.user?.is_active === false;
                            return isInactive && replyingTo.user?.inactive_user_name 
                              ? replyingTo.user.inactive_user_name 
                              : (replyingTo.user?.name || 'Unknown User');
                          })()}
                        </div>
                        <div className="text-sm text-gray-400 truncate">
                          {replyingTo.message}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleCancelReply}
                        className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                        title="Cancel reply"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSendMessage}>
                  <div className="flex items-end gap-2 mb-2 relative">
                    {/* Mobile: Toggle Actions Button */}
                    <div className="lg:hidden relative">
                      <button
                        type="button"
                        onClick={() => setShowMobileActions(!showMobileActions)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 mb-1"
                        title="Actions"
                      >
                        {showMobileActions ? <X className="w-5 h-5 text-gray-400" /> : <Plus className="w-5 h-5 text-gray-400" />}
                      </button>

                      {/* Mobile Actions Popup */}
                      {showMobileActions && (
                        <div className="absolute bottom-full left-0 mb-2 p-1 bg-[#1a1f2e] border border-white/10 rounded-lg shadow-xl flex flex-col gap-1 z-20">
                          <button
                            type="button"
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                            title="Attach file"
                          >
                            <Paperclip className="w-5 h-5 text-gray-400" />
                            <span className="text-white text-xs whitespace-nowrap pr-2">Attach</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowPollModal(true); setShowMobileActions(false); }}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                            title="Create poll"
                          >
                            <BarChart3 className="w-5 h-5 text-gray-400" />
                            <span className="text-white text-xs whitespace-nowrap pr-2">Poll</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setShowListModal(true); setShowMobileActions(false); }}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-2"
                            title="Create list"
                          >
                            <ListOrdered className="w-5 h-5 text-gray-400" />
                            <span className="text-white text-xs whitespace-nowrap pr-2">List</span>
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Desktop Buttons */}
                    <div className="hidden lg:flex items-center gap-1 mb-1">
                      <button
                        type="button"
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Attach file"
                      >
                        <Paperclip className="w-5 h-5 text-gray-400" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowPollModal(true)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Create poll"
                      >
                        <BarChart3 className="w-5 h-5 text-gray-400" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowListModal(true)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        title="Create list"
                      >
                        <ListOrdered className="w-5 h-5 text-gray-400" />
                      </button>
                    </div>
                    <textarea
                      ref={textareaRef}
                      value={messageInput}
                      onChange={handleInputChange}
                      onKeyDown={handleKeyDown}
                      onFocus={handleInputFocus}
                      onClick={handleInputClick}
                      placeholder={`Message #${selectedChannel.name}`}
                      className="flex-1 px-3 py-2 md:px-4 md:py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-white transition-all resize-none overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
                      style={{
                        minHeight: '44px',
                        maxHeight: '144px',
                        height: 'auto',
                        lineHeight: '1.5'
                      }}
                      rows={1}
                      disabled={sendingMessage || !isChannelMember}
                      autoComplete="off"
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 144) + 'px';
                      }}
                    />

                    {/* Mention Autocomplete Popup */}
                    {showMentionPopup && channelMembers.length > 0 && (
                      <div
                        ref={mentionPopupRef}
                        className="absolute bottom-full left-12 mb-2 w-64 max-h-60 bg-[#1a1f2e] border border-white/10 rounded-lg shadow-2xl overflow-hidden z-50"
                      >
                        <div className="p-2 border-b border-white/10">
                          <div className="text-xs font-semibold text-gray-400">Mention a team member</div>
                        </div>
                        <div className="overflow-y-auto max-h-48 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                          {channelMembers
                            .filter(member => {
                              const isInactive = member.user.is_active === false;
                              const displayName = isInactive && member.user.inactive_user_name 
                                ? member.user.inactive_user_name 
                                : (member.user.name || '');
                              return displayName.toLowerCase().includes(mentionSearch);
                            })
                            .map((member, index) => {
                              const isInactive = member.user.is_active === false;
                              const displayName = isInactive && member.user.inactive_user_name 
                                ? member.user.inactive_user_name 
                                : (member.user.name || 'Unknown User');
                              return (
                              <button
                                key={member.user.id}
                                type="button"
                                onClick={() => insertMention(member)}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${index === selectedMentionIndex
                                  ? 'bg-blue-600 text-white'
                                  : 'hover:bg-white/10 text-gray-300'
                                  }`}
                              >
                                <div className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                                  {displayName.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium truncate">{displayName}</div>
                                  {member.user.email && (
                                    <div className="text-xs text-gray-500 truncate">{member.user.email}</div>
                                  )}
                                </div>
                              </button>
                            );
                            })}
                          {channelMembers.filter(m => {
                            const isInactive = m.user.is_active === false;
                            const displayName = isInactive && m.user.inactive_user_name 
                              ? m.user.inactive_user_name 
                              : (m.user.name || '');
                            return displayName.toLowerCase().includes(mentionSearch);
                          }).length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              No members found
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0 mt-1"
                        title="Add emoji"
                      >
                        <Smile className="w-5 h-5 text-gray-400" />
                      </button>

                      {/* Emoji Picker Popup */}
                      {showEmojiPicker && (
                        <div
                          ref={emojiPickerRef}
                          className="absolute bottom-full right-0 mb-2 w-80 max-h-96 bg-black border border-white/20 rounded-lg shadow-2xl overflow-hidden z-50"
                        >
                          <div className="overflow-y-auto max-h-96 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                            {Object.entries(emojiData).map(([category, emojis]) => (
                              <div key={category} className="p-3">
                                <h4 className="text-xs font-semibold text-gray-400 mb-2 sticky top-0 bg-[#1a1f2e] py-1">
                                  {category}
                                </h4>
                                <div className="grid grid-cols-8 gap-1">
                                  {emojis.map((emoji, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => handleEmojiSelect(emoji)}
                                      className="text-2xl hover:bg-white/10 rounded p-1 transition-colors"
                                      title={emoji}
                                    >
                                      {emoji}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Send Button */}
                    <button
                      type="submit"
                      disabled={sendingMessage || !messageInput.trim()}
                      className="p-2 bg-white hover:bg-gray-200 disabled:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex-shrink-0 mt-1"
                      title="Send message"
                    >
                      <Send className="w-5 h-5 text-black" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-500 px-2 mt-1">
                    <span className="hidden lg:inline">Press <span className="text-white font-bold">Enter</span> to send, <span className="text-white font-bold">Shift+Enter</span> for new line. </span>
                    <span>Use <span className="text-white font-bold">@username</span> to mention team members</span>
                  </div>
                </form>
              </div>
            )}

            {/* Members Sidebar */}
            {showMembersSidebar && (
              <>
                {/* Overlay */}
                <div
                  className="absolute inset-0 bg-black/50 z-40"
                  onClick={() => setShowMembersSidebar(false)}
                />

                {/* Sidebar Panel */}
                <div className="absolute right-0 top-0 bottom-0 w-80 bg-black border-l border-white/10 z-50 flex flex-col">
                  {/* Sidebar Header */}
                  <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white">Members</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{channelMembers.length} {channelMembers.length === 1 ? 'member' : 'members'}</p>
                    </div>
                    <button
                      onClick={() => setShowMembersSidebar(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      title="Close"
                    >
                      <X className="w-5 h-5 text-gray-400" />
                    </button>
                  </div>

                  {/* Members List */}
                  <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                    {channelMembers.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        No members in this channel
                      </div>
                    ) : (
                      <div className="p-2">
                        {channelMembers.map((member) => {
                          const isInactive = member.user?.is_active === false;
                          return (
                          <div
                            key={member.user.id}
                            className={`flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors mb-1 ${isInactive ? 'opacity-60' : ''}`}
                          >
                            {/* Avatar */}
                            <div className={`w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white font-semibold flex-shrink-0`}>
                              {member.user.name?.charAt(0).toUpperCase() || 'U'}
                            </div>

                            {/* Member Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-sm font-medium ${isInactive ? 'text-gray-400' : 'text-white'} truncate`}>
                                  {isInactive && member.user.inactive_user_name 
                                    ? member.user.inactive_user_name 
                                    : (member.user.name || 'Unknown User')}
                                </span>
                                {member.role === 'admin' && (
                                  <span className="text-[10px] px-2 py-0.5 bg-white/10 text-white rounded font-bold uppercase tracking-tight">
                                    Admin
                                  </span>
                                )}
                              </div>
                              {member.user.email && (
                                <div className="text-xs text-gray-500 truncate">
                                  {member.user.email}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-black">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Hash className="w-10 h-10 text-gray-500" />
              </div>
              <p className="text-lg font-medium text-gray-400 mb-2">No channel selected</p>
              <p className="text-sm text-gray-500">Choose a channel from the sidebar to start chatting</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateChannelModal
          onClose={() => setShowCreateModal(false)}
          onChannelCreated={handleChannelCreated}
        />
      )}

      {showSettingsModal && selectedChannel && (
        <ChannelSettingsModal
          channel={selectedChannel}
          members={channelMembers}
          onClose={() => setShowSettingsModal(false)}
          onUpdate={loadChannels}
        />
      )}

      {showPollModal && selectedChannel && (
        <CreatePollModal
          channelId={selectedChannel.id}
          onClose={() => setShowPollModal(false)}
          onPollCreated={loadChannelData}
        />
      )}

      {showListModal && selectedChannel && (
        <CreateListModal
          channelId={selectedChannel.id}
          onClose={() => setShowListModal(false)}
          onListCreated={loadChannelData}
        />
      )}
    </div>
  );
};

export default TeamChat;
